// src/pages/PurchaseRecord/purchaseRecordThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import { postData } from "../../../../apiservice/apis";
import { setList, setDetails } from "./PurchaseRecordSlice";

const itemFamily = ["By Item", "By Group", "By Category", "By Tag"];

export const fetchPurchaseRecord = createAsyncThunk(
  "purchaseRecord/fetch",
  async (payload = {}, { dispatch, rejectWithValue }) => {
    try {
      const selFilter =
        payload?.selectedFilterType || payload?.filters?.filterType || "All";
      let selRecord =
        payload?.selectedRecordType ||
        payload?.filters?.recordType ||
        "without item";

      // Decide endpoint
      let url = "/purchase/get-purchase-record";
      if (selRecord === "with item")
        url = "/purchase/get-purchase-record-with-details";
      if (itemFamily.includes(selFilter)) {
        url = "/purchase/get-purchase-details";
        // for item family we treat as "without item" in master sense because endpoint returns item rows
        selRecord = "without item";
      }

      // Build request payload expected by backend
      const req = {
        selectedFilterType: selFilter,
        selectedRecordType: selRecord,
        locationId:
          selFilter === "By Location"
            ? payload?.filters?.locationId || null
            : null,
        supplierId:
          selFilter === "By Supplier"
            ? payload?.filters?.supplierId || null
            : null,
        employeeId:
          selFilter === "By Employee"
            ? payload?.filters?.employeeId || null
            : null,
        itemId:
          selFilter === "By Item" ? payload?.filters?.itemId || null : null,
        groupId:
          selFilter === "By Group" ? payload?.filters?.groupId || null : null,
        categoryId:
          selFilter === "By Category"
            ? payload?.filters?.categoryId || null
            : null,
        userId:
          selFilter === "By User" ? payload?.filters?.userId || null : null,
        voucherNo:
          selFilter === "By Voucher"
            ? payload?.filters?.voucherNo || null
            : null,
        invoiceNo:
          selFilter === "By Invoice"
            ? payload?.filters?.invoiceNo || null
            : null,
        serialNumber:
          selFilter === "By Item Serial"
            ? payload?.filters?.serialNumber || ""
            : "",
        tag_groupnameid:
          selFilter === "By Tag"
            ? payload?.filters?.tag_groupnameid || null
            : null,
        tagFeildNameid:
          selFilter === "By Tag"
            ? payload?.filters?.tagFeildNameid || null
            : null,
        fromDate: payload?.filters?.fromDate || null,
        toDate: payload?.filters?.toDate || null,
      };

      const res = await postData(url, req);

      if (!res || res?.error) {
        return rejectWithValue(res?.error || "Server error");
      }

      let mapped = [];

      // CASE: with item (master with details) -> map into item rows and keep original details
      if (payload?.selectedRecordType === "with item") {
        // res.data is an array of purchase masters with .details array
        mapped = (res.data || []).flatMap((purchase) =>
          (purchase.details || []).map((item, idx) => {
            // serials handling (string or array)
            let serials = [];
            if (item.is_serial === "yes" && item.serials) {
              serials =
                typeof item.serials === "string"
                  ? item.serials
                    .split(",")
                    .filter(Boolean)
                    .map((s) => ({ serial_number: s }))
                  : item.serials;
            }

            const total = Number(
              purchase.total_amount || purchase.total_amount || 0
            );
            const paid = Number(
              purchase.paid_amount ?? purchase.paid_total ?? 0
            );
            const due = Number(purchase.due_amount ?? total - paid);

            return {
              id: `${purchase.pur_id}-${item.item_id}-${idx}`,
              key: `${purchase.pur_id}-${item.item_id}-${idx}`,
              pur_id: purchase.pur_id,
              vch_no: purchase.pur_voucher_no,
              date: purchase.created_date,
              supplier: purchase.acc_name,
              pur_invoice_no: purchase.pur_invoice_no || "",
              employee: purchase.employee_name,
              createdBy: purchase.full_name,
              mode:
                (purchase.trans || []).map((t) => t.to_acc_name).join(", ") ||
                "",
              item_name: item.item_name,
              display_text: item.display_text,
              item_rate: Number(item.item_rate || 0).toFixed(2),
              item_qty: Number(item.item_qty || 0),
              item_discount_per: item.item_discount_per,
              item_tax_per: item.item_tax_per,
              item_total: Number(item.item_total || 0).toFixed(2),

              // master financials
              sub_total: Number(purchase.sub_total || 0).toFixed(2),
              total_discount: Number(purchase.total_discount || 0).toFixed(2),
              total_tax: Number(purchase.total_tax || 0).toFixed(2),
              total_amount: Number(total).toFixed(2),
              paid_total: Number(paid).toFixed(2),
              due_total: Number(due).toFixed(2),
              is_locked: Number(purchase.is_locked) === 1,

              // unit info
              unit_name: item.unit_name,
              unit_symbol: item.unit_symbol,
              base_unit_name: item.base_unit_name,
              retail_qty: Number(item.retail_qty || 0),
              conversion: Number(item.conversion || 1),
              is_serial: item.is_serial,
              serials,

              qty_display: `${item.item_qty || 0} ${item.unit_symbol || ""}${Number(item.conversion || 1) > 1
                ? `, ${item.retail_qty || 0} ${item.base_unit_name || ""}`
                : ""
                }`,
              unitDetails: `Total : Master U. Qty = ${item.item_qty || 0
                }, Base U. Qty = ${item.item_qty || 0}`,
              tatmeen_sent_status: row.tatmeen_sent_status,
              sscc_code: row.sscc_code,
              tatmeen_message_id: row.tatmeen_message_id,
              tatmeen_retry_count: row.tatmeen_retry_count,
              tatmeen_failure_reason: row.tatmeen_failure_reason,
              tatmeen_sscc_status: row.tatmeen_sscc_status,
              tatmeen_manual_action_required: row.tatmeen_manual_action_required,
              tatmeen_last_retry_at: row.tatmeen_last_retry_at,
              tatmeen_retry_count: row.tatmeen_retry_count,
              tatmeen_failure_reason: row.tatmeen_failure_reason,

              unpack_status: row.unpack_status,
              unpacked_at: row.unpacked_at,
              unpack_message_id: row.unpack_message_id,
              unpack_last_error: row.unpack_last_error,
              unpack_retry_count: row.unpack_retry_count,
              unpack_manual_action_required: row.unpack_manual_action_required,
              unpack_last_retry_at: row.unpack_last_retry_at,
            };
          })
        );

        // keep original master+details saved for expand/view
        dispatch(setDetails(res.data || []));
        dispatch(setList(mapped));
      }
      // CASE: item-family filters -> endpoint returns item rows directly
      else if (itemFamily.includes(selFilter) || selFilter === "By Tag") {
        mapped = (res.data || []).map((row, idx) => {
          let serials = [];
          if (row.is_serial === "yes" && row.serials) {
            serials =
              typeof row.serials === "string"
                ? row.serials
                  .split(",")
                  .filter(Boolean)
                  .map((s) => ({ serial_number: s }))
                : row.serials;
          }

          return {
            id: `${row.pur_d_id || row.pur_id}-${row.item_id || idx}`,
            key: `${row.pur_d_id || row.pur_id}-${row.item_id || idx}`,
            pur_d_id: row.pur_d_id,
            pur_id: row.pur_id,
            vch_no: row.pur_voucher_no || row.vch_no,
            date: row.created_date,
            supplier: row.acc_name,
            pur_invoice_no: row.pur_invoice_no || "",
            item_name: row.item_name,
            display_text: row.display_text || row.item_code,
            item_rate: Number(row.item_rate || row.pur_rate || 0).toFixed(2),
            item_qty: Number(row.item_qty || 0),
            item_discount_per: row.item_discount_per || row.item_discount || 0,
            item_tax_per: row.item_tax_per || row.item_tax || 0,
            item_total: Number(row.item_total || 0).toFixed(2),

            group_name: row.group_name,
            category_name: row.category_name,
            tag_name: row.tag_name,
            field_name: row.field_name,
            is_locked: Number(row.is_locked) === 1,

            // units
            unit_name: row.unit_name,
            unit_symbol: row.unit_symbol,
            base_unit_name: row.base_unit_name,
            retail_qty: Number(row.retail_qty || 0),
            conversion: Number(row.conversion || 1),
            is_serial: row.is_serial,
            serials,

            qty_display: `${row.item_qty || 0} ${row.unit_symbol || ""}${Number(row.conversion || 1) > 1
              ? `, ${row.retail_qty || 0} ${row.base_unit_name || ""}`
              : ""
              }`,
            unitDetails: `Total : Master U. Qty = ${row.item_qty || 0
              }, Base U. Qty = ${row.retail_qty || 0}`,
            tatmeen_sent_status: row.tatmeen_sent_status,
            sscc_code: row.sscc_code,
            tatmeen_message_id: row.tatmeen_message_id,
            tatmeen_retry_count: row.tatmeen_retry_count,
            tatmeen_failure_reason: row.tatmeen_failure_reason,
            tatmeen_sscc_status: row.tatmeen_sscc_status,
            tatmeen_manual_action_required: row.tatmeen_manual_action_required,
            tatmeen_last_retry_at: row.tatmeen_last_retry_at,
            tatmeen_retry_count: row.tatmeen_retry_count,
            tatmeen_failure_reason: row.tatmeen_failure_reason,

            unpack_status: row.unpack_status,
            unpacked_at: row.unpacked_at,
            unpack_message_id: row.unpack_message_id,
            unpack_last_error: row.unpack_last_error,
            unpack_retry_count: row.unpack_retry_count,
            unpack_manual_action_required: row.unpack_manual_action_required,
            unpack_last_retry_at: row.unpack_last_retry_at,
          };
        });

        dispatch(setDetails(mapped));
        dispatch(setList(mapped));
      }
      // CASE: without item (summary)
      else {
        mapped = (res.data || []).map((row, idx) => {
          const total = Number(row.total_amount || row.total || 0);
          const paid = Number(row.paid_amount ?? row.paid_total ?? 0);
          const due = Number(row.due_amount ?? total - paid);

          return {
            id: row.pur_id || row.purchase_id,
            key: row.pur_id || idx,
            pur_id: row.pur_id,
            vch_no: row.pur_voucher_no,
            date: row.created_date,
            supplier: row.acc_name,
            pur_invoice_no: row.pur_invoice_no || "",
            employee: row.employee_name,
            createdBy: row.full_name,
            sub_total: Number(row.sub_total || 0).toFixed(2),
            total_discount: Number(row.total_discount || 0).toFixed(2),
            total_tax: Number(row.total_tax || 0).toFixed(2),
            total_amount: Number(total).toFixed(2),
            paid_total: Number(paid).toFixed(2),
            due_total: Number(due).toFixed(2),
            other_service_cost: Number(row.other_service_cost || 0).toFixed(2),
            cgst_amount: row.cgst_amount,
            sgst_amount: row.sgst_amount,
            igst_amount: row.igst_amount,
            is_locked: Number(row.is_locked) === 1,
            tatmeen_sent_status: row.tatmeen_sent_status,
            sscc_code: row.sscc_code,
            tatmeen_message_id: row.tatmeen_message_id,
            tatmeen_retry_count: row.tatmeen_retry_count,
            tatmeen_failure_reason: row.tatmeen_failure_reason,
            tatmeen_sscc_status: row.tatmeen_sscc_status,
            tatmeen_manual_action_required: row.tatmeen_manual_action_required,
            tatmeen_last_retry_at: row.tatmeen_last_retry_at,
            tatmeen_retry_count: row.tatmeen_retry_count,
            tatmeen_failure_reason: row.tatmeen_failure_reason,

            unpack_status: row.unpack_status,
            unpacked_at: row.unpacked_at,
            unpack_message_id: row.unpack_message_id,
            unpack_last_error: row.unpack_last_error,
            unpack_retry_count: row.unpack_retry_count,
            unpack_manual_action_required: row.unpack_manual_action_required,
            unpack_last_retry_at: row.unpack_last_retry_at,
          };
        });

        dispatch(setList(mapped));
        dispatch(setDetails([]));
      }

      return mapped;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err?.message || "Failed");
    }
  }
);

// delete purchase thunk
export const deletePurchase = createAsyncThunk(
  "purchaseRecord/delete",
  async (purId, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await postData("/purchase/purchase-delete", { pur_id: purId });

      if (res?.error) return rejectWithValue(res.message);
      const { filters } = getState().salesRecord;
      dispatch(fetchPurchaseRecord({ filters }));
      return res?.message || "Deleted successfully";
    } catch (err) {
      console.log(err, "res12312355555555");
      return rejectWithValue(err?.response?.data || err?.message || "Failed");
    }
  }
);

export const retryTatmeenPurchase = createAsyncThunk(
  "purchaseRecord/retryTatmeenPurchase",
  async (purchaseId, thunkAPI) => {
    try {
      const response = await postData(
        "/purchase/purchase-manual-retry",
        {
          pur_id: purchaseId,
        }
      );

      const data = response?.data || response;

      console.log("Tatmeen API response:", data);

      if (data?.error === true) {
        return thunkAPI.rejectWithValue(
          data?.message || "Retry failed"
        );
      }

      return data;

    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message ||
        error?.message ||
        "Retry failed"
      );
    }
  }
);


export const unpackPurchase = createAsyncThunk(
  "purchaseRecord/unpackPurchase",
  async (purchaseId, thunkAPI) => {
    try {
      const response = await postData(
        "/purchase/unpack-initiate",
        {
          pur_id: purchaseId,
        }
      );

      const data = response?.data || response;

      if (data?.error) {
        return thunkAPI.rejectWithValue(
          data?.message || "Unpack failed"
        );
      }

      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.error ||
        error.message ||
        "Unpack failed"
      );
    }
  }
);